"use client";

import React, { useState } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ContactFormSchema, ContactFormType } from "@/lib/schema";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileUpload } from "@/components/molecules/file-upload";
import { toast } from "sonner";
import { CheckCircleIcon } from "@heroicons/react/24/outline";

const subjectOptions = [
  { value: "order-issue", label: "Order Issue" },
  { value: "payment-repayment", label: "Payment / Repayment" },
  { value: "refund-chargeback", label: "Refund / Chargeback" },
  { value: "account-login", label: "Account / Login Issue" },
  { value: "vendor-inquiry", label: "Vendor Inquiry" },
  { value: "sales-agent-inquiry", label: "Sales Agent Inquiry" },
  { value: "general-inquiry", label: "General Inquiry" },
  { value: "other", label: "Other" },
];

export function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const methods = useForm<ContactFormType>({
    resolver: zodResolver(ContactFormSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phoneNumber: "",
      subject: "",
      message: "",
    },
  });

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = methods;

  const onSubmit = async (data: ContactFormType) => {
    setIsSubmitting(true);
    try {
      // Simulate API call
      console.log("Form data:", data);
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setIsSubmitted(true);
      toast.success("Request submitted successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className='flex flex-col items-center justify-center space-y-6 py-12 text-center'>
        <div className='flex size-20 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30'>
          <CheckCircleIcon className='size-12 text-green-600 dark:text-green-400' />
        </div>
        <div className='space-y-2'>
          <h2 className='text-text-title text-2xl font-bold'>Thank you for contacting Onyx Support.</h2>
          <p className='text-text-title/70 max-w-md text-lg'>
            We&apos;ve received your request and will get back to you shortly.
          </p>
        </div>
        <Button
          variant='outline'
          onClick={() => {
            setIsSubmitted(false);
            methods.reset();
          }}
          className='mt-4'
        >
          Send another message
        </Button>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className='space-y-8'>
        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
          {/* Full Name */}
          <div className='flex flex-col gap-2.5'>
            <Label htmlFor='fullName'>Full Name (Required)</Label>
            <Input
              id='fullName'
              placeholder='Enter your full name'
              {...register("fullName")}
              aria-invalid={!!errors.fullName}
            />
            {errors.fullName && <p className='text-text-destructive text-xs'>{errors.fullName.message}</p>}
          </div>

          {/* Email Address */}
          <div className='flex flex-col gap-2.5'>
            <Label htmlFor='email'>Email Address (Required)</Label>
            <Input
              id='email'
              type='email'
              placeholder='your@email.com'
              {...register("email")}
              aria-invalid={!!errors.email}
            />
            {errors.email && <p className='text-text-destructive text-xs'>{errors.email.message}</p>}
          </div>

          {/* Phone Number */}
          <div className='flex flex-col gap-2.5'>
            <Label htmlFor='phoneNumber'>Phone Number (Optional)</Label>
            <Input
              id='phoneNumber'
              placeholder='e.g. 08012345678'
              {...register("phoneNumber")}
              aria-invalid={!!errors.phoneNumber}
            />
            {errors.phoneNumber && <p className='text-text-destructive text-xs'>{errors.phoneNumber.message}</p>}
          </div>

          {/* Subject */}
          <div className='flex flex-col gap-2.5'>
            <Label htmlFor='subject'>Subject (Required)</Label>
            <Controller
              name='subject'
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className='h-14 w-full' aria-invalid={!!errors.subject}>
                    <SelectValue placeholder='Select a subject' />
                  </SelectTrigger>
                  <SelectContent>
                    {subjectOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.subject && <p className='text-text-destructive text-xs'>{errors.subject.message}</p>}
          </div>
        </div>

        {/* Message */}
        <div className='flex flex-col gap-2.5'>
          <Label htmlFor='message'>Message (Required)</Label>
          <Textarea
            id='message'
            placeholder='How can we help you?'
            className='min-h-[150px]'
            {...register("message")}
            aria-invalid={!!errors.message}
          />
          {errors.message && <p className='text-text-destructive text-xs'>{errors.message.message}</p>}
        </div>

        {/* Attachment */}
        <div className='flex flex-col gap-2.5'>
          <Label>Upload File (Optional)</Label>
          <Controller
            name='attachment'
            control={control}
            render={({ field }) => (
              <FileUpload
                onFileChange={field.onChange}
                acceptedFormats={[".jpg", ".jpeg", ".png", ".pdf"]}
                maxSize={5 * 1024 * 1024}
                error={errors.attachment?.message as string}
              />
            )}
          />
        </div>

        <Button type='submit' className='w-full py-7 text-lg' disabled={isSubmitting} loading={isSubmitting}>
          Submit Request
        </Button>
      </form>
    </FormProvider>
  );
}
